import { recurseBuildCRFields } from '@smart-builder/util';
import { createMainResourceREST } from '../resource';
import {
  ChargeableActions,
  ChargeableState,
  CRChargeableBody,
  CRChargeableBodyAPI,
  CRChargeableKey,
  CRChargeableSummary,
  CRChargeableSummaryAPI,
} from './types';

type AnyChargeable = CRChargeableKey | CRChargeableBody | undefined;

function getEndpointChargeable(key: AnyChargeable): string {
  return key ? `/chargeables/${key.chargeableId}/` : '/chargeables/';
}

function sameChargeable(a: AnyChargeable, b: AnyChargeable): boolean {
  return a?.chargeableId === b?.chargeableId;
}

function mergeChargeable(
  last: CRChargeableBody,
  current: CRChargeableBody,
  isUpdate: boolean,
  lastSeqNum?: number,
  curSeqNum?: number,
) {
  if (current.scene && last.scene && lastSeqNum === curSeqNum) {
    // we are loading different parts of the same chargeable, merge together.

    // parsing the scene and updating the WebGL model is expensive, avoid it if possible.
    const scene =
      isUpdate || current.scene?.sceneIdentifier !== last.scene?.sceneIdentifier
        ? current.scene || last.scene
        : last.scene || current.scene;
    const drawing = current.drawing ?? last.drawing;
    return {
      ...last,
      ...current,
      scene,
      drawing,
    };
  } else if (
    current.drawing &&
    last.drawing &&
    // InteractiveSVGs will always do a 2nd get after any new svg. This is to ensure we have the latest drawing items
    // with actual linkedValues (see comment below). We need to always take the result of this 2nd GET as is. Otherwise
    // we'll end up with stale linkedValue fields.
    // If the response was in response to an update, then the seqNum must have changed. Therefore only do the below
    // in that situation.
    lastSeqNum !== curSeqNum
  ) {
    // We clear links on every update. The links are only rebuilt upon a SVG being drawn. This has the consequence of
    // the linkedValue not being returned in the result of patch requests. We don't want the field to pop in and
    // out of existence, as this causes the visual field to also do a disappearing act. As such -
    // try use the last linkedValue if available.
    return {
      ...current,
      ...recurseBuildCRFields(
        { drawing: current.drawing },
        { drawing: last.drawing },
        current => current,
        (merged, current, last) => {
          if (
            last?.linkedAttributeFieldName &&
            !merged.linkedAttributeFieldName
          ) {
            return {
              ...merged,
              linkedValue: last.linkedValue,
              linkedAttributeFieldName: last.linkedAttributeFieldName,
              linkedAttributeOwnerPath: last.linkedAttributeOwnerPath,
              linkedAttributeEditable: last.linkedAttributeEditable,
            };
          } else {
            return merged;
          }
        },
      ),
    };
  } else {
    return current;
  }
}

function mergeSummaryBody(
  oldSummary?: CRChargeableSummary,
  curBody?: CRChargeableBody,
): CRChargeableSummary {
  return (curBody ?? oldSummary)!;
}

function parseChargeableBody(body: CRChargeableBodyAPI): CRChargeableBody {
  return body;
}

function parseChargeableSummary(
  summary: CRChargeableSummaryAPI,
): CRChargeableSummary {
  return summary;
}

const chargeableStateSelector = (state: any): ChargeableState =>
  state.chargeable;

const currentChargeableSelector = (state: any): CRChargeableBody | undefined =>
  state.chargeable.currentBody;

const { actions, reducer, watches, rawActions, useListener, useWizard } =
  createMainResourceREST(
    'chargeable',
    getEndpointChargeable,
    chargeableStateSelector,
    sameChargeable,
    parseChargeableBody,
    parseChargeableSummary,
    mergeChargeable,
    mergeSummaryBody,
  );

const chargeableActions: ChargeableActions = actions;
export {
  chargeableActions,
  useListener as useChargeableListener,
  useWizard as useChargeableWizard,
  reducer as chargeableReducer,
  watches as chargeableWatches,
  chargeableStateSelector,
  currentChargeableSelector,
  rawActions as chargeableRawActions,
};

export * from './types';
